// =============================================================================
// MeuDriver — CookieConsent (Batch L3)
// Banner de consentimento de cookies — LGPD.
// =============================================================================

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { X, Cookie } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'

const STORAGE_KEY = 'cookie_consent_accepted'

type ConsentChoice = 'all' | 'essential' | null

export function CookieConsent() {
  const [choice, setChoice] = useState<ConsentChoice>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) {
        // Pequeno delay para não conflitar com first-paint
        const t = setTimeout(() => setVisible(true), 800)
        return () => clearTimeout(t)
      }
    } catch {
      // localStorage pode não estar disponível (modo privado extremo)
    }
  }, [])

  function accept(value: 'all' | 'essential') {
    setChoice(value)
    setVisible(false)
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      // silently ignore
    }
  }

  if (choice !== null) return null

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            'fixed bottom-0 left-0 right-0 z-[200]',
            'bg-white border-t border-gray-200 shadow-2xl',
          )}
          role="dialog"
          aria-label="Consentimento de cookies"
          aria-live="polite"
        >
          <div className="max-w-5xl mx-auto px-4 py-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Ícone + texto */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Cookie className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Usamos cookies para melhorar sua experiência
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Cookies essenciais mantêm a plataforma funcionando. Cookies analíticos
                    (opcionais) nos ajudam a entender como você usa o serviço.{' '}
                    <Link
                      to="/privacidade"
                      className="text-brand-600 hover:underline font-medium"
                    >
                      Saiba mais
                    </Link>
                  </p>
                </div>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8"
                  onClick={() => accept('essential')}
                  data-testid="cookie-essential"
                >
                  Apenas essenciais
                </Button>
                <Button
                  size="sm"
                  className="text-xs h-8 bg-brand-600 hover:bg-brand-700"
                  onClick={() => accept('all')}
                  data-testid="cookie-accept-all"
                >
                  Aceitar todos
                </Button>
                <button
                  onClick={() => accept('essential')}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
